import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, CreditCard } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "@apollo/client/react";
import { CREATE_PREPAID_TOPUP_CHECKOUT_SESSION } from "@/graphql/mutations";
import { trackDashboardEvent } from "@/lib/analytics";

interface TopUpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTopUp: () => void;
  enabled: boolean;
  unavailableReason?: string | null;
}

const CREDITS_PER_USD = 100;
const MAX_CUSTOM_CREDITS = 9_999_999_999_999;

const priceForCredits = (credits: number) => (credits / CREDITS_PER_USD).toFixed(2);

// Pricing: $1 = 100 Flow Credits
const packages = [
  { amount: 2500, price: 25, popular: false },
  { amount: 5000, price: 50, popular: true },
  { amount: 10000, price: 100, popular: false },
];

const TopUpDialog = ({
  open,
  onOpenChange,
  onTopUp,
  enabled,
  unavailableReason,
}: TopUpDialogProps) => {
  const minimumCustomCredits = packages[2].amount;
  const clampCustomCredits = (credits: number) =>
    Math.min(Math.max(credits, minimumCustomCredits), MAX_CUSTOM_CREDITS);
  const [selected, setSelected] = useState(packages[1].amount);
  const [customAmount, setCustomAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const [createPrepaidTopupCheckoutSession] = useMutation(CREATE_PREPAID_TOPUP_CHECKOUT_SESSION);

  const handlePurchase = async () => {
    setIsLoading(true);

    try {
      if (!enabled) {
        toast({
          title: "Top-ups unavailable",
          description: unavailableReason || "Prepaid top-ups are not available right now.",
          variant: "destructive",
        });
        return;
      }

      const credits = customAmount
        ? clampCustomCredits(parseInt(customAmount) || 0)
        : selected;

      trackDashboardEvent("billing_begin_checkout", {
        billing_flow: "top_up",
        credits,
        amount_usd: Number(priceForCredits(credits)),
      });

      const { data } = await createPrepaidTopupCheckoutSession({
        variables: {
          input: { credits },
        },
      });

      const response = data as {
        createPrepaidTopupCheckoutSession?: {
          success?: boolean;
          url?: string | null;
          error?: string | null;
        } | null;
      };

      if (!response?.createPrepaidTopupCheckoutSession?.success || !response.createPrepaidTopupCheckoutSession.url) {
        trackDashboardEvent("billing_result", {
          billing_flow: "top_up",
          result: "failure",
          credits,
          error_message: response?.createPrepaidTopupCheckoutSession?.error || "checkout_unavailable",
        });
        toast({
          title: "Checkout unavailable",
          description: response?.createPrepaidTopupCheckoutSession?.error || "Failed to start checkout. Please try again.",
          variant: "destructive",
        });
        return;
      }

      onOpenChange(false);
      onTopUp();
      trackDashboardEvent("billing_result", {
        billing_flow: "top_up",
        result: "redirected",
        credits,
      });
      window.location.assign(response.createPrepaidTopupCheckoutSession.url);
    } catch (error) {
      console.error("Error starting prepaid top-up checkout:", error);
      trackDashboardEvent("billing_result", {
        billing_flow: "top_up",
        result: "failure",
        error_message: error instanceof Error ? error.message : "checkout_error",
      });
      toast({
        title: "Error",
        description: "An error occurred while starting checkout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-2xl">Top Up Credits</DialogTitle>
          <DialogDescription>
            Choose a package to add more Flow Credits to your account
          </DialogDescription>
        </DialogHeader>

        {!enabled && (
          <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
            <p className="text-sm text-amber-900 dark:text-amber-100">
              {unavailableReason || "Prepaid top-ups are unavailable right now."}
            </p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4 py-4">
          {packages.map((pkg) => (
            <button
              key={pkg.amount}
              onClick={() => {
                setSelected(pkg.amount);
                setCustomAmount("");
              }}
              className={`relative p-6 rounded-lg border-2 transition-all duration-200 text-left ${selected === pkg.amount
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50 bg-muted/30"
                }`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-4 px-3 py-1 bg-gradient-creative text-white text-xs font-semibold rounded-full">
                  Popular
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold">{pkg.amount.toLocaleString()}</span>
                  <span className="text-muted-foreground">credits</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-primary">${pkg.price}</span>
                  <span className="text-sm text-muted-foreground">USD</span>
                </div>

              </div>

              {selected === pkg.amount && (
                <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
            </button>
          ))}

          {/* Custom Amount Option */}
          <div
            className={`relative p-6 rounded-lg border-2 transition-all duration-200 ${customAmount
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50 bg-muted/30"
              }`}
          >
            <div className="space-y-3">
              <div className="text-xl font-bold">Custom Amount</div>
              <div className="space-y-2">
                <Label htmlFor="custom-amount" className="text-sm">Enter credits</Label>
                <Input
                  id="custom-amount"
                  type="number"
                  min={minimumCustomCredits.toString()}
                  max={MAX_CUSTOM_CREDITS.toString()}
                  value={customAmount}
                  onFocus={() => {
                    // Set to the minimum custom amount when user first focuses on custom input.
                    if (!customAmount) {
                      setCustomAmount(minimumCustomCredits.toString());
                      setSelected(minimumCustomCredits);
                    }
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) {
                      setCustomAmount("");
                      return;
                    }

                    const numValue = Math.min(parseInt(value) || 0, MAX_CUSTOM_CREDITS);
                    setCustomAmount(numValue.toString());
                    setSelected(Math.max(numValue, minimumCustomCredits));
                  }}
                  onBlur={(e) => {
                    // When user leaves the field, enforce both min and max.
                    const value = e.target.value;
                    if (value) {
                      const boundedValue = clampCustomCredits(parseInt(value) || 0);
                      setCustomAmount(boundedValue.toString());
                      setSelected(boundedValue);
                    }
                  }}
                  placeholder={`Min. ${minimumCustomCredits.toLocaleString()} credits ($${priceForCredits(minimumCustomCredits)})`}
                  className="bg-background [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              {customAmount && (
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-primary">${priceForCredits(parseInt(customAmount) || 0)}</span>
                  <span className="text-sm text-muted-foreground">USD</span>
                </div>
              )}
            </div>

            {customAmount && (
              <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePurchase}
            disabled={isLoading || !enabled}
            className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
          >
            <CreditCard className="mr-2 h-4 w-4" />
            {isLoading ? "Redirecting..." : `Buy ${selected.toLocaleString()} credits`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TopUpDialog;
